import { Form, Input, Select, Space } from 'antd';
import Papa from 'papaparse';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import Api from '../../../api/index';
import { Images } from '../../../assets/images/Index';
import { LanguageModel, SellerModel } from '../../../interfaces';
import { ImportFilter } from '../../../interfaces/models/Import.model';
import '../../../sass/components/_user-container.scss';
import { toastNotification } from '../../../utils/toastNotification';
import { exportColumns, ExportColumnsTypes } from './csv_data';
import { Filters } from './Filters';
import { actions } from './helper';
import { ListingImportData } from './ListingImport';

const LocalSellerImport = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [selectType, setSelectedType] = useState(false);
  const [importData, setImportData] = useState<any[]>([]);
  const [languages, setLanguages] = useState<LanguageModel[]>([]);
  const [sellers, setSellers] = useState<SellerModel[]>([]);
  const [action, setAction] = useState<keyof ExportColumnsTypes>('action');
  const [selectedValues, setSelectedValues] = useState<ImportFilter>({
    language: 'en',
  });

  const handleSetError = (msg: string) => {
    toastNotification('error', msg);
    setLoading(false);
    setLoadingImport(false);
  };

  const handleSelectAction = (value: string) => {
    setImportData([]);
    form.resetFields();
    setAction(value as keyof ExportColumnsTypes);
    setSelectedType(true);
  };

  const handleFile = (file: File) => {
    const promise = new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = e => {
        const bstr = e?.target?.result;
        const wb = XLSX.read(bstr, { type: 'buffer' });
        /* Get first worksheet */
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        /* Convert array of arrays */
        const fileAsArray = XLSX.utils.sheet_to_json(ws, { header: 1 });
        fileAsArray.shift();
        resolve(fileAsArray);
      };
      reader.onerror = error => {
        reject(error);
      };
    });
    promise.then((data: any) => {
      const structuredData = data.map((item: any) => ({
        id: item[0],
        product_id: item[1],
        sku: `RDU-${item[2]}`,
        ean: item[3],
        name: item[4],
        seller: item[5],
        rating: item[6],
        cncode: item[7],
        stock: Number(item[8] || 0),
        purchase_price: item[9],
        price: item[10],
        discount_price: item[11] || 0,
        tvsh: item[12],
      }));
      setImportData(structuredData);
    });
  };

  const handleOnChange = ({ target: { files } }: ChangeEvent<HTMLInputElement>) => {
    if (files && files?.length) {
      setLoading(true);
      Papa.parse(files[0], {
        header: true,
        skipEmptyLines: true,
        delimiter: ',',
        encoding: 'utf8',
        complete: async function (results: any) {
          let data: any;
          if (exportColumns[action] != 'action') {
            switch (action.toString()) {
              case 'translate_products': {
                data = results.data.map((result: any) => ({
                  sku: result?.sku,
                  name: result?.name,
                  short_description: result?.short_description || ' ',
                  long_description: result?.long_description || ' ',
                }));
                break;
              }
              case 'products': {
                data = results.data.map((result: any) => ({
                  sku: getCompaniesCode(result?.sku),
                  product_number: result?.sku,
                  name: result?.name,
                  ean: result?.ean,
                  image: result?.image,
                  long_description: result?.description || ' ',
                  length: result?.length || '0',
                  weight: result?.weight || '0',
                  width: result?.width || '0',
                  height: result?.height || '0',
                  price: result?.price || '0',
                  stock: result?.stock || '0',
                  rating: result?.rating || 0,
                }));

                break;
              }
              case 'products_price': {
                handleFile(files[0]);
                break;
              }
              default:
                break;
            }
          }

          setImportData(data);
          setLoading(false);
        },
      });
    }
  };

  const getCompaniesCode = (sku: string) => {
    // TODO: what if a company has 3 first charchters the same
    const selectedSeller = sellers.find(element => element.id === selectedValues?.seller);
    const cmpName = selectedSeller?.name.substring(0, 3)?.toUpperCase() + '-';
    return cmpName + sku;
  };

  const handleOnSubmit = () => {
    setLoadingImport(true);
    if (exportColumns[action] != 'action') {
      switch (action.toString()) {
        case 'translate_products': {
          // Import translations
          Api(`import/translate/${selectedValues?.language}/${selectedValues?.seller}`, 'POST', {
            data: importData,
          })
            .then(({ statusIsOk, statusMessage, result }) => {
              if (!statusIsOk) handleSetError(statusMessage);
              if (statusIsOk) {
                setLoadingImport(false);
                toastNotification('success', `Sucessfully updated ${result.length} products`);
              }
            })
            .catch(({ statusMessage }) => handleSetError(statusMessage));
          break;
        }
        case 'products': {
          // Import products
          Api(
            `import/products/import/${selectedValues?.language}/${selectedValues?.seller}`,
            'POST',
            { data: importData },
          )
            .then(({ statusIsOk, statusMessage, result }) => {
              if (!statusIsOk) handleSetError(statusMessage);
              if (statusIsOk) {
                setLoadingImport(false);
                toastNotification('success', `Sucessfully updated ${result.length} products`);
              }
            })
            .catch(({ error }) => handleSetError(error));
          break;
        }
        case 'products_price': {
          // Import products pricing
          Api(`import/products/pricing`, 'POST', {
            data: importData?.map((item: any) => ({
              stock: Number(item.stock || 0),
              tvsh: item?.tvsh || 18,
              id: item?.id,
              price: item?.price,
              sku: item?.sku,
              net_price: 0,
              discount_price: item?.discount_price || 0,
              ...(item?.discount_price && {
                discount_price: item?.discount_price || 0,
              }),
              product: {
                id: item?.product_id,
                is_updated: true,
              },
            })),
          })
            .then(({ statusIsOk, statusMessage }) => {
              if (!statusIsOk) handleSetError(statusMessage);
              if (statusIsOk) {
                setLoadingImport(false);
                toastNotification('success', `Sucessfully updated products`);
              }
            })
            .catch(({ statusMessage }) => handleSetError(statusMessage));
          break;
        }
        default:
          break;
      }
    }
  };

  const handleGetAllLanguages = () => {
    Api('language', 'GET', null)
      .then(({ statusIsOk, statusMessage, result }) => {
        if (!statusIsOk) toastNotification('error', statusMessage);
        if (statusIsOk) setLanguages(result.results);
      })
      .catch(({ statusMessage }) => toastNotification('error', statusMessage));
  };

  const handleGetAllSellers = () => {
    Api('seller', 'GET', null)
      .then(({ statusIsOk, statusMessage, result }) => {
        if (!statusIsOk) toastNotification('error', statusMessage);
        if (statusIsOk) {
          setSellers(result);
        }
      })
      .catch(({ statusMessage }) => handleSetError(statusMessage));
  };

  useEffect(() => {
    handleGetAllSellers();
    handleGetAllLanguages();
  }, []);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header import-design-header">
        <div>
          <h2>{t('import')}</h2>
        </div>
      </div>
      <div className="dashboard-header import-design-header">
        <div>
          <Space align="center">
            <Input.Group className="import-design-form">
              <Select
                placeholder="Select action"
                onChange={handleSelectAction}
                size="large"
                suffixIcon={<img src={Images.arrowCDown} />}
                getPopupContainer={trigger => trigger.parentNode}
              >
                {actions.map(({ id, name, url, colName }) => (
                  <Select.Option value={colName} key={id}>
                    {name}
                  </Select.Option>
                ))}
              </Select>
            </Input.Group>
          </Space>
        </div>
      </div>
      <div className="dashboard-header import-design-header">
        <Filters
          form={form}
          setSearchValue={setSelectedValues}
          searchValue={selectedValues}
          languages={languages}
          actionData={action}
          importData={handleOnSubmit}
          handleOnChange={handleOnChange}
          loadingImport={loadingImport}
          action={action}
        />
      </div>

      {selectType && <ListingImportData loading={loading} data={importData} action={action} />}
    </div>
  );
};

export default LocalSellerImport;
